import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// utils
import { capitalize } from '../../utils/text';

// Appt Components
import Box from '../../components/simple/box/Box';
import DataTable from '../../components/simple/dataTable/DataTable.js';
import Text from '../../components/simple/text/Text';

// Services
import FormService from '../../services/FormService';
import LocalStorageService from '../../services/LocalStorageService';
import { Anchor } from 'grommet';
import ActionButton from '../../components/compound/actionButton/ActionButton.js';

const SmartPlanSearchResults = props => {
  const apiToken = LocalStorageService.get('apiToken');
  const activeOrg = LocalStorageService.get('activeOrg');
  const terms = LocalStorageService.get('terms')

  const [submissions, setSubmissions] = useState([]);
  const [responses, setResponses] = useState([]);

  const navigate = useNavigate();

  const deleteReport = async submissionId => {
    if (window.confirm('Are you sure you want to delete this?')) {
      await FormService.updateSubmission(apiToken, submissionId, {
        status: 'Archived',
      });
      window.location.reload(); // this is not ideal
    }
  };
  
  const [sort, setSort] = useState({
      property: 'reference',
      direction: 'desc'
    })
  const resultColumns = [
    {
      property: 'reference',
      header: <Text>Plan No</Text>,
      render: datum => (
        <Anchor onClick={e => {
          e.preventDefault();
          navigate(`/pdfviewer?url=${encodeURIComponent(datum.pdfUrl)}`);
        }}>
          <Text>{datum.id ? `besmart-${datum.reference}` : ''}</Text>
        </Anchor>
      ),
    },
    {
      property: 'firstName',
      header: (
        <Text>{capitalize(terms?.serviceUser || 'Service User')}</Text>
      ),
      render: datum => (
        <Anchor href={`/serviceuser/${datum.userId}/edit`} >
          <Text>{`${datum.firstname || ''} ${datum.lastname || ''}`}</Text>
        </Anchor>
      ),
    },
    {
      property: 'yearGroup',
      header: <Text>Year Group</Text>,
      render: datum => (
        <Anchor href={`/serviceusers/year/${datum.yearGroup}`} >
          <Text>{`${datum.yearGroup || ''}`}</Text>
        </Anchor>
      ),
    },
    {
      property: 'createdAt',
      header: <Text>Plan Date</Text>,
      render: datum => (
        <Text>
          {moment(datum.createdAt).format('DD/MM/YY') === 'Invalid date'
            ? 'No Date'
            : moment(datum.createdAt).format('DD/MM/YY')}
        </Text>
      ),
    },
    {
      property: 'flag',
      header: <Text>Flag</Text>,
      render: datum => {
        const setColour = async color => {
          await FormService.updateSubmission(apiToken, datum.id, {
            flag: color,
          });

          Array.from(
            document.getElementsByClassName(`flag-${datum.id}`),
          ).forEach(function (element, index, array) {
            element.style.opacity = 0.15;
          });

          document.getElementById(
            `flag-${datum.id}-${color}`,
          ).style.opacity = 1;
        };

        return (
          <Box direction="row" width="100%">
            <ActionButton plain onClick={() => setColour('none')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-none`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#777"
                style={
                  !['red', 'green', 'yellow'].includes(datum.flag)
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('green')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-green`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#00FF00"
                style={
                  'green' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('yellow')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-yellow`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#FFA500"
                style={
                  'yellow' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('red')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-red`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#FF0000"
                style={
                  'red' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
          </Box>
        );
      },
    },
    {
      property: 'actions',
      header: <Text>Actions</Text>,
      render: datum => (
        <>
          <Box direction="row" width="100%">
            {props?.type !== 'smartplan' && (
              <ActionButton
                onClick={() => {
                  navigate(`/incident/${datum.userId}/edit/${datum.id}`);
                }}
                target="_self"
                plain
                children={
                  <Box pad="5px">
                    <FontAwesomeIcon icon={['fal', 'edit']} color="#ff8300" />
                  </Box>
                }
              />
            )}

           {datum.pdfUrl && <ActionButton
            plain
            href={"/pdfviewer?url=" + encodeURIComponent(datum.pdfUrl)}
            children={
              <Box pad="5px">
                <FontAwesomeIcon
                  icon={['fal', 'eye']}
                  color="#ff8300"
                />
                {/* <Download color='primary' size='medium' disabled /> */}
              </Box>
            }
          />}

            {datum.pdfUrl && (
              <>
                <ActionButton
                  target="_blank"
                  plain
                  href={datum.pdfUrl}
                  children={
                    <Box pad="5px">
                      <FontAwesomeIcon
                        icon={['fal', 'download']}
                        color="#ff8300"
                      />
                      {/* <Download color='primary' size='medium' disabled /> */}
                    </Box>
                  }
                />
                <ActionButton
                  target="_blank"
                  plain
                  onClick={() => {
                    deleteReport(datum.id);
                  }}
                  children={
                    <Box pad="5px">
                      <FontAwesomeIcon
                        icon={['fal', 'trash']}
                        color="#ff8300"
                      />
                    </Box>
                  }
                />
              </>
            )}
          </Box>
        </>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={resultColumns}
        data={props.results}
        primaryKey="id"
        step={250}
        paginate={true}
        sort={sort}
        onSort={setSort}
      />
      {!props.results.length && <Text>There are no results to display.</Text>}
    </>
  );
};

export default SmartPlanSearchResults;
